import { makeAutoObservable } from 'mobx';
import environment from '@environment';
import { GotvAccountType } from 'core/constants/gotv-account-types';
import { ResidentialAccountType } from 'core/constants/residential-account-types';
import { SitePlatform } from 'core/enums/site-platform';
import { FinancialAccount } from 'core/models/account/account-details.dto';
import { Account } from 'core/models/account/amount-to-pay.dto';
import { SiteConfig } from 'core/models/site-config';
import { Util } from 'utils/common.utils';

const ChannelFromPlatform = {
  [SitePlatform.dstv]: environment.MAH_API_CHANNEL,
  [SitePlatform.gotv]: environment.MAH_API_CHANNEL_GOTV,
};

const BusinessUnitFromPlatform = {
  [SitePlatform.dstv]: environment.MAH_API_BUSINESS_UNIT,
  [SitePlatform.gotv]: environment.MAH_API_BUSINESS_UNIT_GOTV,
};

export const FriendlyPlatformNameMap = {
  [SitePlatform.dstv]: 'DStv',
  [SitePlatform.gotv]: 'GOtv',
};

export const SiteConfigStore: SiteConfig = {
  isInitialized: false,
  allowCrossPlatform: true,

  userBasedPlatform: undefined,
  urlBasedPlatform: undefined,

  channel: () => (
    ChannelFromPlatform[SiteConfigStore.platform()] || environment.MAH_API_CHANNEL
  ),

  businessUnit: () => (
    BusinessUnitFromPlatform[SiteConfigStore.platform()] || environment.MAH_API_BUSINESS_UNIT
  ),

  connectClientId: () => (
    SiteConfigStore.platform() === SitePlatform.gotv ? environment.CONNECT_GOTV_CLIENT_ID : environment.CONNECT_DSTV_CLIENT_ID
  ),

  intendedPlatform: () => (
    SiteConfigStore.allowCrossPlatform
      ? SiteConfigStore.platform()
      : SitePlatform.dstv
  ),

  platform: () => (
    SiteConfigStore.userBasedPlatform ||
    SiteConfigStore.urlBasedPlatform ||
    SitePlatform.dstv
  ),

  platformMissMatch: () => (
    SiteConfigStore.userBasedPlatform !== undefined &&
    SiteConfigStore.userBasedPlatform !== SiteConfigStore.urlBasedPlatform
  ),

  initialize: () => {
    if (SiteConfigStore.isInitialized) {
      return SiteConfigStore;
    }

    SiteConfigStore.isInitialized = true;

    makeAutoObservable(SiteConfigStore);

    return SiteConfigStore;
  },

  changeAllowCrossPlatform: (allow: boolean) => {
    SiteConfigStore.allowCrossPlatform = allow;
  },

  changeUserBasedPlatform: (userBasedPlatform: SitePlatform) => {
    SiteConfigStore.userBasedPlatform = userBasedPlatform;
  },

  changeUrlBasedPlatform: (urlBasedPlatform: SitePlatform) => {
    SiteConfigStore.urlBasedPlatform = urlBasedPlatform;
  },

  isAccountType: (acc: Account | FinancialAccount): acc is Account => {
    return (<Account>acc).accountType !== undefined;
  },

  getAccountType: (acc: Account | FinancialAccount) => {
    return SiteConfigStore.isAccountType(acc) ? acc.accountType : acc.type;
  },

  getPlatform: (accounts: Account[]): SitePlatform => {
    const gotvAccount = accounts.find((acc) =>
      GotvAccountType.includes(acc.accountType)
    );

    return (gotvAccount) ? SitePlatform.gotv : SitePlatform.dstv;
  },

  getAccount: <T extends Account | FinancialAccount>(accounts: T[] = []): T | undefined => {
    if (SiteConfigStore.platform() === SitePlatform.gotv) {
      return accounts.find((acc) =>
        GotvAccountType.includes(SiteConfigStore.getAccountType(acc))
      );
    }

    return accounts.find((acc) =>
      ResidentialAccountType.includes(SiteConfigStore.getAccountType(acc))
    );
  },

  getAllAccounts: <T extends Account | FinancialAccount>(accounts: T[] = []): T[] | undefined => {
    if (SiteConfigStore.platform() === SitePlatform.gotv) {
      return accounts.filter((acc) =>
        GotvAccountType.includes(SiteConfigStore.getAccountType(acc))
      );
    }

    return accounts.filter((acc) =>
      ResidentialAccountType.includes(SiteConfigStore.getAccountType(acc))
    );
  },

  reset: () => {
    SiteConfigStore.isInitialized = false;
    SiteConfigStore.allowCrossPlatform = true;
    SiteConfigStore.userBasedPlatform = undefined;
    SiteConfigStore.urlBasedPlatform = Util.GetConfigFromURL().platform;
  }
}